/**
 * Formate un prix en euros
 * @param {number} amount - Montant en euros
 * @param {boolean} showDecimals - Afficher les décimales
 * @returns {string} Prix formaté (ex: "6 900€")
 */
export function formatPrice(amount, showDecimals = false) {
  if (typeof amount !== 'number') return '0€';

  const formatter = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  });

  return formatter.format(amount).replace(/\s/g, ' ');
}

/**
 * Formate une date
 * @param {string} dateString - Date au format ISO (YYYY-MM-DD)
 * @param {string} format - 'short', 'long', ou 'full'
 * @returns {string} Date formatée
 */
export function formatDate(dateString, format = 'long') {
  if (!dateString) return '';

  const date = new Date(dateString);

  const formats = {
    short: { day: 'numeric', month: 'short', year: 'numeric' },
    long: { day: 'numeric', month: 'long', year: 'numeric' },
    full: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
  };

  return new Intl.DateTimeFormat('fr-FR', formats[format] || formats.long).format(date);
}

/**
 * Calcule la différence en mois entre deux dates
 * @param {string} startDate - Date de début (YYYY-MM-DD)
 * @param {string} endDate - Date de fin (YYYY-MM-DD)
 * @returns {number} Nombre de mois
 */
export function getMonthsDifference(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const months = (end.getFullYear() - start.getFullYear()) * 12 +
                 (end.getMonth() - start.getMonth());

  return Math.max(0, months);
}

/**
 * Formate une durée en jours
 * @param {number} days - Nombre de jours
 * @returns {string} Durée formatée (ex: "23 jours")
 */
export function formatDuration(days) {
  if (days === 0) return '0 jour';
  if (days === 1) return '1 jour';
  if (days < 1) return `${Math.round(days * 8)}h`;
  return `${days} jours`;
}

/**
 * Formate un pourcentage
 * @param {number} value - Valeur du pourcentage
 * @param {boolean} showSign - Afficher le signe +/-
 * @returns {string} Pourcentage formaté (ex: "-25%")
 */
export function formatPercent(value, showSign = false) {
  const sign = showSign && value > 0 ? '+' : '';
  return `${sign}${value}%`;
}

/**
 * Génère un slug à partir d'une chaîne
 * @param {string} text - Texte à convertir
 * @returns {string} Slug
 */
export function slugify(text) {
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Tronque un texte
 * @param {string} text - Texte à tronquer
 * @param {number} maxLength - Longueur max
 * @returns {string} Texte tronqué
 */
export function truncate(text, maxLength = 100) {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
}

/**
 * Calcule le montant d'une réduction
 * @param {number} price - Prix initial
 * @param {number} discount - Pourcentage de réduction
 * @returns {number} Prix après réduction
 */
export function calculateDiscount(price, discount) {
  return Math.round(price * (1 - discount / 100));
}
